import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { SearchKind, SearchResult } from '@hesabat/contracts';
import { api, UnauthorizedError } from './api';
import { Icon, type IconName } from './icons';
import { formatMoney } from './money';
import { SCREENS, type ViewKind } from './nav';
import { SHORTCUTS } from './routes';
import { matchesSearch } from './search';

/** وجهة من البحث — شاشة بمعاملاتها، و`App` يحوّلها لعرض. */
export type SearchTarget =
  | { kind: 'screen'; screen: ViewKind }
  | { kind: 'customers'; customerId: string }
  | { kind: 'suppliers'; supplierId: string }
  | { kind: 'invoices' | 'receipts'; query?: string; customerId?: string }
  | { kind: 'bills' | 'payments'; query?: string; supplierId?: string }
  | { kind: 'catalog'; query: string }
  | { kind: 'ledger'; accountCode: string };

interface Props {
  companyId: string;
  /** الشاشات المسموحة لهذا المستخدم — لا يقترح البحث شاشة مخفية عنه. */
  screens: readonly ViewKind[];
  /** `journal.post`: إجراءات «فاتورة جديدة» و«تحصيل» تظهر لمن يسجّل المستندات. */
  canPost: boolean;
  onOpen: (target: SearchTarget) => void;
  onSessionLost: () => void;
}

interface Entry {
  key: string;
  icon: IconName;
  title: string;
  subtitle: string | null;
  meta: string | null;
  target: SearchTarget;
  actions: { label: string; target: SearchTarget }[];
}

const GROUPS: Record<SearchKind, { label: string; icon: IconName }> = {
  customer: { label: 'العملاء', icon: 'users' },
  supplier: { label: 'الموردون', icon: 'truck' },
  item: { label: 'الأصناف', icon: 'tag' },
  account: { label: 'الحسابات', icon: 'ledger' },
  sales_invoice: { label: 'فواتير المبيعات', icon: 'invoice' },
  purchase_bill: { label: 'فواتير الموردين', icon: 'bill' },
  receipt: { label: 'التحصيلات', icon: 'receive' },
  payment: { label: 'السداد للموردين', icon: 'send' },
};

/** إجراءات تُكتب بالكلمة: «فاتورة» تقترح فاتورة جديدة، «تحصيل» تقترح تحصيلًا. */
const ACTIONS: readonly { title: string; words: string; screen: ViewKind; post: boolean }[] = [
  {
    title: 'فاتورة مبيعات جديدة',
    words: 'فاتوره بيع مبيعات جديده',
    screen: 'invoices',
    post: true,
  },
  { title: 'تحصيل من عميل', words: 'تحصيل قبض ايصال استلام', screen: 'receipts', post: true },
  { title: 'فاتورة مورد جديدة', words: 'فاتوره مورد شراء مشتريات', screen: 'bills', post: true },
  { title: 'سداد لمورد', words: 'سداد دفع صرف مورد', screen: 'payments', post: true },
  { title: 'مصروف نقدى', words: 'مصروف مصاريف نقدي', screen: 'expenses', post: true },
  { title: 'قيد يومية', words: 'قيد يوميه تسويه', screen: 'journal', post: false },
  { title: 'عميل جديد', words: 'عميل جديد اضافه', screen: 'customers', post: true },
  { title: 'مورد جديد', words: 'مورد جديد اضافه', screen: 'suppliers', post: true },
];

function entryOf(result: SearchResult, canPost: boolean): Entry {
  const group = GROUPS[result.kind];
  const meta =
    [result.date, result.amount ? formatMoney(result.amount) : null].filter(Boolean).join(' • ') ||
    null;
  const base = {
    key: result.kind + ':' + result.id,
    icon: group.icon,
    title: result.title,
    subtitle: result.subtitle,
    meta,
  };
  const party = result.partyId;
  switch (result.kind) {
    case 'customer':
      return {
        ...base,
        target: { kind: 'customers', customerId: result.id },
        actions: canPost
          ? [
              { label: 'فاتورة جديدة', target: { kind: 'invoices', customerId: result.id } },
              { label: 'تحصيل', target: { kind: 'receipts', customerId: result.id } },
            ]
          : [],
      };
    case 'supplier':
      return {
        ...base,
        target: { kind: 'suppliers', supplierId: result.id },
        actions: canPost
          ? [
              { label: 'فاتورة مورد', target: { kind: 'bills', supplierId: result.id } },
              { label: 'سداد', target: { kind: 'payments', supplierId: result.id } },
            ]
          : [],
      };
    case 'item':
      return { ...base, target: { kind: 'catalog', query: result.title }, actions: [] };
    case 'account':
      return {
        ...base,
        target: { kind: 'ledger', accountCode: result.code ?? '' },
        actions: [],
      };
    case 'sales_invoice':
    case 'receipt':
      return {
        ...base,
        target: { kind: result.kind === 'receipt' ? 'receipts' : 'invoices', query: result.title },
        actions: party
          ? [{ label: 'كشف العميل', target: { kind: 'customers', customerId: party } }]
          : [],
      };
    case 'purchase_bill':
    case 'payment':
      return {
        ...base,
        target: { kind: result.kind === 'payment' ? 'payments' : 'bills', query: result.title },
        actions: party
          ? [{ label: 'كشف المورد', target: { kind: 'suppliers', supplierId: party } }]
          : [],
      };
  }
}

/**
 * البحث الموحّد: زرار فى الشريط العلوى واختصار Ctrl+K (أو «/»). يلاقى الشاشات والإجراءات
 * فورًا من المتصفح، والعملاء والموردين والأصناف والحسابات والمستندات من الخادم. كل نتيجة
 * تفتح مكانها، وجنبها إجراءات سريعة (فاتورة جديدة للعميل، كشف المورد…).
 */
export default function GlobalSearch({
  companyId,
  screens,
  canPost,
  onOpen,
  onSessionLost,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const lost = useRef(onSessionLost);
  lost.current = onSessionLost;

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      const typing =
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName));
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === '/' && !typing) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActive(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  // طلب واحد بعد توقف الكتابة، والرد القديم يُتجاهل لو اتكتب غيره
  useEffect(() => {
    const text = query.trim();
    if (!open || text.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = window.setTimeout(() => {
      api
        .search(companyId, text)
        .then((value) => {
          if (alive) setResults(value.results);
        })
        .catch((caught: unknown) => {
          if (caught instanceof UnauthorizedError) lost.current();
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [companyId, open, query]);

  const sections = useMemo(() => {
    const text = query.trim();
    const out: { label: string; entries: Entry[] }[] = [];
    const actions = ACTIONS.filter(
      (action) =>
        screens.includes(action.screen) &&
        (!action.post || canPost) &&
        text !== '' &&
        matchesSearch(text, [action.title, action.words]),
    ).map<Entry>((action) => ({
      key: 'action:' + action.title,
      icon: 'plus',
      title: action.title,
      subtitle: null,
      meta: null,
      target: { kind: 'screen', screen: action.screen },
      actions: [],
    }));
    if (actions.length) out.push({ label: 'إجراءات', entries: actions });
    const pages = screens
      .filter(
        (screen) => !text || matchesSearch(text, [SCREENS[screen].title, SCREENS[screen].hint]),
      )
      .slice(0, text ? 5 : 12)
      .map<Entry>((screen) => ({
        key: 'screen:' + screen,
        icon: SCREENS[screen].icon,
        title: SCREENS[screen].title,
        subtitle: SCREENS[screen].hint,
        meta: null,
        target: { kind: 'screen', screen },
        actions: [],
      }));
    for (const kind of Object.keys(GROUPS) as SearchKind[]) {
      const entries = results
        .filter((item) => item.kind === kind)
        .map((item) => entryOf(item, canPost));
      if (entries.length) out.push({ label: GROUPS[kind].label, entries });
    }
    if (pages.length) out.push({ label: 'الشاشات', entries: pages });
    return out;
  }, [query, results, screens, canPost]);

  const flat = sections.flatMap((section) => section.entries);

  function go(target: SearchTarget): void {
    setOpen(false);
    onOpen(target);
  }

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') setOpen(false);
    else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && flat[active]) {
      event.preventDefault();
      go(flat[active].target);
    }
  }

  useEffect(() => setActive(0), [query, results]);

  let index = -1;
  return (
    <>
      <button
        type="button"
        className="global-search-trigger"
        onClick={() => setOpen(true)}
        aria-label="بحث فى البرنامج كله"
        title="بحث فى البرنامج كله (Ctrl+K)"
      >
        <Icon name="search" size={18} />
        <span>ابحث عن عميل، فاتورة، صنف…</span>
        <kbd>Ctrl K</kbd>
      </button>
      {open &&
        createPortal(
          <div className="global-search-overlay" onMouseDown={() => setOpen(false)}>
            <div
              className="global-search"
              role="dialog"
              aria-modal="true"
              aria-label="بحث"
              onMouseDown={(event) => event.stopPropagation()}
              onKeyDown={onKeyDown}
            >
              <div className="global-search-input">
                <Icon name="search" />
                <input
                  ref={input}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="اسم عميل أو مورد، رقم فاتورة، صنف، تليفون، مبلغ، أو اسم شاشة"
                  aria-label="نص البحث"
                />
                {loading && <span className="sub">جارى البحث…</span>}
              </div>
              <div className="global-search-results" role="listbox">
                {sections.map((section) => (
                  <div key={section.label} className="global-search-group">
                    <p className="label">{section.label}</p>
                    {section.entries.map((entry) => {
                      index += 1;
                      const mine = index;
                      return (
                        <div
                          key={entry.key}
                          className={'global-search-item' + (mine === active ? ' active' : '')}
                          role="option"
                          aria-selected={mine === active}
                          onMouseEnter={() => setActive(mine)}
                        >
                          <button type="button" className="main" onClick={() => go(entry.target)}>
                            <Icon name={entry.icon} size={18} />
                            <span className="text">
                              <strong>{entry.title}</strong>
                              {entry.subtitle && <span className="sub">{entry.subtitle}</span>}
                            </span>
                            {entry.meta && <span className="meta num-cell">{entry.meta}</span>}
                          </button>
                          {entry.actions.map((action) => (
                            <button
                              key={action.label}
                              type="button"
                              className="ghost quick"
                              onClick={() => go(action.target)}
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {query.trim().length >= 2 && !loading && sections.length === 0 && (
                  <p className="sub empty">
                    مفيش عميل أو مورد أو صنف أو مستند بالنص ده — جرّب جزء من الاسم أو الرقم.
                  </p>
                )}
              </div>
              <p className="global-search-hint sub">
                {query.trim() === ''
                  ? SHORTCUTS.filter((item) => screens.includes(item.kind))
                      .map((item) => 'Alt+' + item.key + ' ' + SCREENS[item.kind].title)
                      .concat('Alt+← رجوع')
                      .join(' • ')
                  : '↑ ↓ للتنقل • Enter للفتح • Esc للإغلاق'}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
